import darkAnimation from "assets/animations/Lens-Empty-Dark.json";
import lightAnimation from "assets/animations/Lens-Empty-Light.json";
import { ExpandableCard } from "collections/Cards";
import { DesktopDeviceFormSettings } from "Constants";
import { Animation, PolyIcon, ToolTip } from "elements";
import { infiniteNestLink, useQueryParamString } from "polyrouter";
import React from "react";
import { useSelector } from "react-redux";
import { ProtectedLink } from "TsRoleGuards";
import { useTranslation } from "utilities/i18n";
import { State } from "store";
import { DesktopDeviceFormSetting } from "./DesktopDeviceSettings";
import { useDiscardSetting, useRevertSetting } from "./MyDesktopDeviceSettings";
import { ProvisionedCategorySetting } from "./ProvisionedDesktopDeviceSettings";
import {
  DesktopDevicePolicyScope,
  useDesktopDevicePolicyScopeContext
} from "./ProvisionedDesktopDeviceSettingsRouter";
import { generateC10String } from "./utilities/c10";

type DesktopDeviceSettingsMapperProps = {
  view: string;
  inheritedBy: string;
  settingsToDisplay: DesktopDeviceFormSettings;
  showInheritance?: boolean;
};

interface SettingProps {
  setting: DesktopDeviceFormSetting;
}

const PolicySetting: React.FC<SettingProps> = ({ setting }) => {
  const [state] = useDesktopDevicePolicyScopeContext();
  const revertSetting = useRevertSetting();
  const inheritance = useSelector(
    (state: State) =>
      state.desktopDeviceSettingInheritance[generateC10String(setting.accessor)]
  );
  const hasRevert =
    inheritance?.scope === state.desktopDevicePolicyScope ||
    state.desktopDevicePolicyScope !== DesktopDevicePolicyScope.Device;
  const onRevert = hasRevert
    ? () => revertSetting(setting.accessor, setting.type)
    : undefined;
  return (
    <ProvisionedCategorySetting
      key={setting.accessor}
      revert={{
        action: onRevert
      }}
      {...setting}
    />
  );
};

const ChangedSetting: React.FC<SettingProps> = ({ setting }) => {
  const resetSettingChange = useDiscardSetting();
  return (
    <ProvisionedCategorySetting
      key={setting.accessor}
      revert={{
        action: () => resetSettingChange(setting.accessor),
        icon: "clear_circle_outline",
        iconClassName: "outline"
      }}
      {...setting}
    />
  );
};

const Category: React.FC<{
  name: string;
  settings: DesktopDeviceFormSetting[];
  expanded: boolean;
}> = ({ name, settings, expanded }) => {
  const { t } = useTranslation();
  const [view] = useQueryParamString<"view">("view");
  const changesView = view === "changes";
  const categories: { [name: string]: string } = {
    LANGUAGE: t("LANGUAGE"),
    GENERAL: t("GENERAL"),
    RINGTONES_AND_VOLUME: t("RINGTONES_AND_VOLUME"),
    AUDIO: t("AUDIO"),
    SENSORS_AND_PRESENCE: t("SENSORS_AND_PRESENCE"),
    SOFTPHONES_AND_MEDIA_PLAYERS: t("SOFTPHONES_AND_MEDIA_PLAYERS"),
    WIRELESS: t("WIRELESS"),
    ADVANCED: t("SETTINGS_ADVANCED"),
    VIDEO_CONTROLS: t("VIDEO_CONTROLS"),
    DIAGNOSTICS: t("DIAGNOSTICS"),
    SOFTWARE_UPDATE: t("SOFTWARE_UPDATE")
  };
  return (
    <ExpandableCard
      collapsedContent={
        <div className="category-title pl-5">{categories[name]}</div>
      }
      dropdown
      expanded={expanded}
    >
      <section className="my-settings-category">
        {settings.map(setting => (
          <div key={setting.accessor}>
            {changesView ? (
              <ChangedSetting setting={setting} />
            ) : (
              <PolicySetting setting={setting} />
            )}
          </div>
        ))}
      </section>
    </ExpandableCard>
  );
};

export const DesktopDeviceSettingsMapper: React.FC<DesktopDeviceSettingsMapperProps> = ({
  view,
  inheritedBy,
  settingsToDisplay,
  showInheritance
}) => {
  const { t } = useTranslation();
  const categories = Object.entries(settingsToDisplay);
  const hasSettings = categories.length > 0;

  if (!hasSettings) {
    return (
      <div data-testid="no-results">
        {view === "changes" ? (
          <div className="no-results-indicator">
            <img src="/img/astrounaut.png" alt="" />
            <p className="text-md">{t("NO_CHANGED_SETTINGS")}</p>
          </div>
        ) : (
          <Animation
            animation={{
              light: lightAnimation,
              dark: darkAnimation
            }}
            title={t("NO_POLICY_SETTINGS")}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div className="my-settings-info mb-15">
        {view === "changes"
          ? t("UNSAVED_CHANGES_INFO")
          : t("MY_SETTINGS_POLICY_INFO", { devices: inheritedBy })}
      </div>
      <div className="form grid scrollable" data-testid="my-settings-view">
        <div className="headers mb-1 grid">
          <span className="col-8 pl-5">{t("CATEGORY")}</span>
          {showInheritance ? (
            <span className="col-2">
              {t("POLICY_INHERITANCE")}
              <ToolTip
                iconTitle="info"
                iconReplacement={
                  <PolyIcon icon="info_circle" className="ml-5" />
                }
                position="bottom"
                size="lg"
              >
                <span>{t("MY_SETTINGS_INHERITANCE_INFO")}</span>
              </ToolTip>
            </span>
          ) : (
            <span className="col-2"></span>
          )}
          {view === "changes" ? null : (
            <span className="col-2">
              <ProtectedLink
                to={infiniteNestLink("/remove")}
                className="remove-all"
                requirement="update:device"
              >
                {t("REMOVE_ALL")}
              </ProtectedLink>
            </span>
          )}
        </div>
        {
          <>
            {categories.map(([category, categorySettings], index) => (
              <Category
                name={category}
                settings={categorySettings}
                key={`${view}-${category}`}
                expanded={index === 0}
              />
            ))}
          </>
        }
      </div>
    </>
  );
};
